import type { ApiClient } from '@tadpoles/api-client'
import type { ReadyRosieVideo, Story, StoryPage, StoryWithPages } from '@tadpoles/common'

import { requireStoryTitle } from '../../utils/story/require-story-title.js'
import { requireReadyRosieVideoTitle } from '../../utils/video/require-ready-rosie-video-title.js'
import { AsyncSingleFlight } from '../../utils/async-single-flight.js'
import { watchSettled } from '../../utils/watch-settled.svelte.js'
import type { Connectivity } from '../connectivity/service.js'
import type { FamilyLibraryEntitlements } from '../feature.svelte.js'
import { CacheWritingService } from '../persistence/cache-writing-service.js'
import type { ServiceLifecycle } from '../service-lifecycle.js'
import type { CurrentUserScopedServices } from '../user-scoped-service-scope.js'
import {
    decodeLibraryCatalogMetadata,
    decodePersistedStories,
    decodePersistedVideos,
    LibraryCategories,
    type LibraryCatalogBacking,
    type LibraryCategory
} from './catalog.js'
import { type LibraryCatalogs, ReactiveLibraryState } from './state.svelte.js'

export type LibraryServiceOptions = {
    apiClient: Pick<ApiClient, 'library'>
    catalogBacking?: LibraryCatalogBacking
    connectivity?: Connectivity
    currentUserServices: CurrentUserScopedServices<'feature'>
    initiallyOnline?: boolean
    now?: () => number
    reportError?: (error: unknown) => void
}

const NoFamilyLibraryEntitlements: FamilyLibraryEntitlements = Object.freeze({
    ebook: false,
    enabled: false,
    video: false
})

type LibraryServiceLifecycleState =
    | 'uninitialized'
    | 'initializing'
    | 'active'
    | 'suspended'
    | 'destroying'
    | 'destroyed'

type StoryChanges = {
    pagesByStoryId: ReadonlyMap<string, readonly StoryPage[]>
    removedStoryIds: string[]
    stories: Story[]
}

export class LibraryService extends CacheWritingService implements ServiceLifecycle {
    readonly #apiClient: Pick<ApiClient, 'library'>
    readonly #catalogBacking: LibraryCatalogBacking | undefined
    readonly #catalogRefreshes = new Map<LibraryCategory, Promise<void>>()
    readonly #connectivity: Connectivity | undefined
    readonly #currentUserServices: CurrentUserScopedServices<'feature'>
    readonly #destroyEntitlementWatch: () => void
    readonly #destroyFlight = new AsyncSingleFlight()
    readonly #initializeFlight = new AsyncSingleFlight()
    readonly #lifetimeController = new AbortController()
    readonly #now: () => number
    readonly #persistenceTasks = new Set<Promise<void>>()
    readonly #refreshTimestamps = new Map<LibraryCategory, number>()
    readonly #reportError: (error: unknown) => void
    #lifecycleState: LibraryServiceLifecycleState = 'uninitialized'
    #observedOffline = false
    #online: boolean
    readonly #state = new ReactiveLibraryState()
    #unregisterConnectivity: (() => void) | undefined

    public constructor(options: LibraryServiceOptions) {
        super()
        this.#apiClient = options.apiClient
        this.#catalogBacking = options.catalogBacking
        this.#connectivity = options.connectivity
        this.#currentUserServices = options.currentUserServices
        this.#now = options.now ?? Date.now
        this.#online = options.connectivity?.online ?? options.initiallyOnline ?? true
        this.#observedOffline = !this.#online
        this.#reportError =
            options.reportError ?? ((error) => console.warn('Library error.', error))
        this.#destroyEntitlementWatch = watchSettled(
            () => this.entitlements,
            (current, previous) => this.#refreshNewlyEntitledCatalogs(current, previous)
        )
    }

    public get catalogs(): LibraryCatalogs {
        return this.#state.catalogs
    }

    public get pagesByStoryId(): ReadonlyMap<string, readonly StoryPage[]> {
        return this.#state.pagesByStoryId
    }

    public get entitlements(): FamilyLibraryEntitlements {
        return (
            this.#currentUserServices.currentUserServices?.feature.libraryEntitlements ??
            NoFamilyLibraryEntitlements
        )
    }

    public async initialize(): Promise<void> {
        this.#assertAvailable()

        const activeInitialization = this.#initializeFlight.active
        if (activeInitialization !== undefined) {
            await activeInitialization
            return
        }

        if (this.#lifecycleState === 'active' || this.#lifecycleState === 'suspended') {
            return
        }

        await this.#initializeFlight.run(async () => {
            if (this.#lifecycleState !== 'uninitialized') {
                return
            }

            this.#lifecycleState = 'initializing'

            try {
                await Promise.all([this.#restoreStories(), this.#restoreVideos()])

                if (
                    this.#lifecycleState !== 'initializing' ||
                    this.#lifetimeController.signal.aborted
                ) {
                    return
                }

                if (this.#connectivity !== undefined) {
                    this.#online = this.#connectivity.online
                    this.#observedOffline = !this.#online
                }
                this.#unregisterConnectivity = this.#connectivity?.register(this)
                this.#lifecycleState = 'active'
                await this.#refreshEntitledCatalogs()
            } catch (error) {
                if (this.#lifecycleState === 'initializing') {
                    this.#lifecycleState = 'uninitialized'
                }
                throw error
            }
        })
    }

    public async resume(): Promise<void> {
        this.#assertInitialized()
        const wasSuspended = this.#lifecycleState === 'suspended'
        this.#lifecycleState = 'active'

        if (wasSuspended && this.#online) {
            await this.#refreshEntitledCatalogs()
        }
    }

    public suspend(): void {
        this.#assertInitialized()
        this.#lifecycleState = 'suspended'
    }

    public async destroy(): Promise<void> {
        const activeDestruction = this.#destroyFlight.active
        if (activeDestruction !== undefined) {
            await activeDestruction
            return
        }

        if (this.#lifecycleState === 'destroyed') {
            return
        }

        this.#lifecycleState = 'destroying'
        await this.#destroyFlight.run(async () => {
            this.#destroyEntitlementWatch()
            this.#unregisterConnectivity?.()
            this.#unregisterConnectivity = undefined
            this.#lifetimeController.abort()
            await Promise.allSettled([
                ...(this.#initializeFlight.active === undefined
                    ? []
                    : [this.#initializeFlight.active]),
                ...this.#catalogRefreshes.values(),
                ...this.#persistenceTasks
            ])
            this.#catalogRefreshes.clear()
            this.#persistenceTasks.clear()
            this.#refreshTimestamps.clear()
            this.#state.reset()
            this.#lifecycleState = 'destroyed'
        })
    }

    public async setOnline(online: boolean): Promise<void> {
        this.#assertInitialized()

        if (!online) {
            this.#online = false
            this.#observedOffline = true
            return
        }

        const shouldRefresh = !this.#online && this.#observedOffline
        this.#online = true
        this.#observedOffline = false

        if (shouldRefresh && this.#lifecycleState === 'active') {
            await this.#refreshEntitledCatalogs()
        }
    }

    public async retry(category: LibraryCategory): Promise<void> {
        this.#assertInitialized()

        if (!this.#isEntitled(category) || !this.#online || this.#lifecycleState !== 'active') {
            return
        }

        await this.#refreshCatalog(category)
    }

    async #restoreStories(): Promise<void> {
        const backing = this.#catalogBacking
        if (backing === undefined) {
            return
        }

        try {
            const value = await backing.loadStories(this.#lifetimeController.signal)
            this.#lifetimeController.signal.throwIfAborted()

            if (value !== null) {
                const changes = this.#decodeStoryChanges(decodePersistedStories(value))
                if (changes.removedStoryIds.length > 0) {
                    throw new Error('Persisted Library stories cannot contain deleted records.')
                }
                this.#publishStories(changes.stories, changes.pagesByStoryId)
                await this.#restoreMetadata(LibraryCategories.ebook)
            }
        } catch (error) {
            if (!this.#lifetimeController.signal.aborted) {
                this.#reportError(error)
            }
        }
    }

    async #restoreVideos(): Promise<void> {
        const backing = this.#catalogBacking
        if (backing === undefined) {
            return
        }

        try {
            const value = await backing.loadVideos(this.#lifetimeController.signal)
            this.#lifetimeController.signal.throwIfAborted()

            if (value !== null) {
                this.#publishVideos(this.#applyVideoChanges([], decodePersistedVideos(value)))
                await this.#restoreMetadata(LibraryCategories.video)
            }
        } catch (error) {
            if (!this.#lifetimeController.signal.aborted) {
                this.#reportError(error)
            }
        }
    }

    async #restoreMetadata(category: LibraryCategory): Promise<void> {
        const value = await this.#catalogBacking?.loadMetadata(
            category,
            this.#lifetimeController.signal
        )
        this.#lifetimeController.signal.throwIfAborted()
        if (value !== null && value !== undefined) {
            const metadata = decodeLibraryCatalogMetadata(value)
            this.#refreshTimestamps.set(category, metadata.refreshTimestamp)
        }
    }

    async #refreshEntitledCatalogs(): Promise<void> {
        if (!this.#online || this.#lifecycleState !== 'active') {
            return
        }

        const categories = this.#categories().filter((category) => this.#isEntitled(category))
        await Promise.allSettled(
            categories.map(async (category) => await this.#refreshCatalog(category))
        )
    }

    #refreshCatalog(category: LibraryCategory): Promise<void> {
        const existingRefresh = this.#catalogRefreshes.get(category)

        if (existingRefresh !== undefined) {
            return existingRefresh
        }

        const refresh = this.#loadCatalog(category)
        this.#catalogRefreshes.set(category, refresh)
        void refresh.finally(() => {
            if (this.#catalogRefreshes.get(category) === refresh) {
                this.#catalogRefreshes.delete(category)
            }
        })
        return refresh
    }

    async #loadCatalog(category: LibraryCategory): Promise<void> {
        const current = this.#state.catalogs[category]
        this.#state.replaceStatus(category, {
            hasUsableCatalog: current.hasUsableCatalog,
            phase: 'loading'
        })

        try {
            if (category === LibraryCategories.ebook) {
                await this.#loadStories()
            } else {
                await this.#loadVideos()
            }
        } catch (error) {
            if (this.#lifetimeController.signal.aborted) {
                return
            }

            this.#state.replaceStatus(category, {
                failure: {
                    blocking: !current.hasUsableCatalog,
                    retryable: true
                },
                hasUsableCatalog: current.hasUsableCatalog,
                phase: 'failure'
            })
            this.#reportError(error)
        }
    }

    async #loadStories(): Promise<void> {
        const previousRefreshTimestamp = this.#refreshTimestamps.get(LibraryCategories.ebook)
        const refreshTimestamp = this.#now()
        const records = await this.#loadAllPages(async (cursor) => {
            const response = await this.#apiClient.library.stories.list(
                { cursor, timestamp: previousRefreshTimestamp },
                { signal: this.#lifetimeController.signal }
            )
            return { cursor: response.cursor, items: response.stories }
        })
        this.#lifetimeController.signal.throwIfAborted()
        const changes = this.#decodeStoryChanges(records)
        const snapshot = this.#applyStoryChanges(
            previousRefreshTimestamp === undefined ? [] : this.#state.catalogs.ebook.items,
            previousRefreshTimestamp === undefined ? new Map() : this.#state.pagesByStoryId,
            changes
        )
        this.#publishStories(snapshot.stories, snapshot.pagesByStoryId)
        this.#refreshTimestamps.set(LibraryCategories.ebook, refreshTimestamp)
        this.#persistStories(snapshot.stories, snapshot.pagesByStoryId, refreshTimestamp)
    }

    async #loadVideos(): Promise<void> {
        const previousRefreshTimestamp = this.#refreshTimestamps.get(LibraryCategories.video)
        const refreshTimestamp = this.#now()
        const records = await this.#loadAllPages(async (cursor) => {
            const response = await this.#apiClient.library.videos.list(
                { cursor, timestamp: previousRefreshTimestamp },
                { signal: this.#lifetimeController.signal }
            )
            return { cursor: response.cursor, items: response.readyRosieVideos }
        })
        this.#lifetimeController.signal.throwIfAborted()
        const videos = this.#applyVideoChanges(
            previousRefreshTimestamp === undefined ? [] : this.#state.catalogs.video.items,
            records
        )
        this.#publishVideos(videos)
        this.#refreshTimestamps.set(LibraryCategories.video, refreshTimestamp)
        this.#persistVideos(videos, refreshTimestamp)
    }

    #decodeStoryChanges(records: StoryWithPages[]): StoryChanges {
        const identifiers = new Set<string>()
        const pagesByStoryId = new Map<string, readonly StoryPage[]>()
        const removedStoryIds: string[] = []
        const stories: Story[] = []

        for (const record of records) {
            if (identifiers.has(record.id)) {
                throw new Error('Library Story changes contain duplicate identifiers.')
            }
            identifiers.add(record.id)

            if (record.deleted) {
                removedStoryIds.push(record.id)
                continue
            }

            const { pages, ...story } = record
            requireStoryTitle(story)
            stories.push(story)
            pagesByStoryId.set(story.id, pages)
        }

        return { pagesByStoryId, removedStoryIds, stories }
    }

    #applyStoryChanges(
        currentStories: ReadonlyArray<Readonly<Story>>,
        currentPagesByStoryId: ReadonlyMap<string, readonly StoryPage[]>,
        changes: StoryChanges
    ): { pagesByStoryId: ReadonlyMap<string, readonly StoryPage[]>; stories: Story[] } {
        const pagesByStoryId = new Map(currentPagesByStoryId)
        const storiesByIdentifier = new Map(currentStories.map((story) => [story.id, story]))

        for (const identifier of changes.removedStoryIds) {
            storiesByIdentifier.delete(identifier)
            pagesByStoryId.delete(identifier)
        }

        for (const story of changes.stories) {
            storiesByIdentifier.set(story.id, story)
            const pages = changes.pagesByStoryId.get(story.id)
            if (pages === undefined) {
                throw new Error(`Library Story ${story.id} changes do not contain pages.`)
            }
            pagesByStoryId.set(story.id, pages)
        }

        return { pagesByStoryId, stories: [...storiesByIdentifier.values()] }
    }

    #applyVideoChanges(
        current: ReadonlyArray<Readonly<ReadyRosieVideo>>,
        changes: ReadyRosieVideo[]
    ): ReadyRosieVideo[] {
        const videosByIdentifier = new Map(current.map((video) => [video.id, video]))
        const changedIdentifiers = new Set<string>()

        for (const video of changes) {
            if (changedIdentifiers.has(video.id)) {
                throw new Error('Library Video changes contain duplicate identifiers.')
            }
            changedIdentifiers.add(video.id)
            requireReadyRosieVideoTitle(video)
            videosByIdentifier.set(video.id, video)
        }

        return [...videosByIdentifier.values()]
    }

    async #loadAllPages<Item>(
        loadPage: (cursor?: string) => Promise<{ cursor?: string; items: Item[] }>
    ): Promise<Item[]> {
        const items: Item[] = []
        let cursor: string | undefined

        do {
            const page = await loadPage(cursor)
            items.push(...page.items)
            cursor = page.cursor
        } while (cursor !== undefined)

        return items
    }

    #publishStories(
        stories: Story[],
        pagesByStoryId: ReadonlyMap<string, readonly StoryPage[]>
    ): void {
        this.#state.replaceStories(stories, pagesByStoryId, {
            hasUsableCatalog: true,
            phase: 'available'
        })
    }

    #publishVideos(videos: ReadyRosieVideo[]): void {
        this.#state.replaceVideos(videos, {
            hasUsableCatalog: true,
            phase: 'available'
        })
    }

    #persistStories(
        stories: Story[],
        pagesByStoryId: ReadonlyMap<string, readonly StoryPage[]>,
        refreshTimestamp: number
    ): void {
        const backing = this.#catalogBacking
        if (backing === undefined) {
            return
        }

        const records = stories.map((story) => {
            const pages = pagesByStoryId.get(story.id)
            if (pages === undefined) {
                throw new Error(`Library Story ${story.id} does not have retained pages.`)
            }
            return { ...story, pages: [...pages] }
        })

        void this.#trackPendingPersistence(
            this.enqueueCacheWrite(async () => {
                await backing.persistStories(
                    records,
                    { refreshTimestamp },
                    this.#lifetimeController.signal
                )
            })
        )
    }

    #persistVideos(videos: ReadyRosieVideo[], refreshTimestamp: number): void {
        const backing = this.#catalogBacking
        if (backing === undefined) {
            return
        }

        void this.#trackPendingPersistence(
            this.enqueueCacheWrite(async () => {
                await backing.persistVideos(
                    videos,
                    { refreshTimestamp },
                    this.#lifetimeController.signal
                )
            })
        )
    }

    #trackPendingPersistence(task: Promise<void>): Promise<void> {
        const trackedTask = task.catch((error) => {
            if (!this.#lifetimeController.signal.aborted) {
                this.#reportError(error)
            }
        })
        this.#persistenceTasks.add(trackedTask)
        void trackedTask.finally(() => {
            this.#persistenceTasks.delete(trackedTask)
        })
        return trackedTask
    }

    #isEntitled(category: LibraryCategory): boolean {
        return this.entitlements[category]
    }

    #refreshNewlyEntitledCatalogs(
        current: FamilyLibraryEntitlements,
        previous: FamilyLibraryEntitlements
    ): void {
        if (this.#lifecycleState !== 'active' || !this.#online) {
            return
        }

        for (const category of this.#categories()) {
            if (current[category] && !previous[category]) {
                void this.#refreshCatalog(category)
            }
        }
    }

    #categories(): LibraryCategory[] {
        return [LibraryCategories.video, LibraryCategories.ebook]
    }

    #assertInitialized(): void {
        this.#assertAvailable()

        if (this.#lifecycleState !== 'active' && this.#lifecycleState !== 'suspended') {
            throw new Error('Library service must be initialized before use.')
        }
    }

    #assertAvailable(): void {
        if (this.#lifecycleState === 'destroying' || this.#lifecycleState === 'destroyed') {
            throw new Error('Library service has been destroyed.')
        }
    }
}
